import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPuzzlePiece,
  faChartLine,
  faHeadset,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";

const WhyUs = ({ darkMode = false, language = "en" }) => {
  const translations = {
    en: {
      sectionTitle: "Why Choose Infynix Solution?",
      sectionSubtitle: "Your trusted partner for technology excellence.",
      reasons: [
        {
          icon: faPuzzlePiece,
          title: "End-to-End Solutions",
          desc: "We offer complete, integrated IT solutions under one roof—eliminating the need for multiple vendors and ensuring seamless execution.",
        },
        {
          icon: faChartLine,
          title: "Proven Track Record",
          desc: "Backed by years of experience and successful project deliveries, we consistently exceed client expectations across industries and technologies.",
        },
        {
          icon: faHeadset,
          title: "24/7 Customer Support",
          desc: "Our expert support team is available around the clock to resolve your technical issues swiftly and keep your business running smoothly.",
        },
        {
          icon: faDollarSign,
          title: "Competitive Pricing",
          desc: "We provide top-tier services at cost-effective rates, ensuring maximum return on investment without compromising on quality or performance.",
        },
      ],
      services: [
        {
          title: "VPS Server & Cloud Services",
          theory:
            "Reliable VPS hosting and scalable cloud infrastructure with enterprise-level security, full customization, and 99.9% uptime to keep your business online without interruptions.",
          image: "/images/services/VPS.png",
          imagePosition: "left",
        },
        {
          title: "WhatsApp Chatbot & Communication",
          theory:
            "Smart AI-driven WhatsApp chatbots and Business API integration that automate customer service, streamline communication, and generate leads effortlessly.",
          image: "/images/home/WhatsApp.png",
          imagePosition: "right",
        },
        {
          title: "ERP Implementation & Consulting",
          theory:
            "End-to-end ERP implementation and expert consulting to optimize your operations, improve productivity, and achieve seamless digital transformation.",
          image: "/images/services/ERPConsulting.png",
          imagePosition: "left",
        },
        {
          title: "AI Automation",
          theory:
            "Cutting-edge AI automation solutions that reduce manual work, increase efficiency, and unlock smarter decision-making for your business.",
          image: "/images/services/AIAutomation.jpg",
          imagePosition: "right",
        },
        {
          title: "Website Development",
          theory:
            "Modern, responsive, and SEO-friendly websites designed to deliver exceptional user experience and grow your digital presence.",
          image: "/images/services/Mobile.png",
          imagePosition: "left",
        },
        {
          title: "Mobile App Development & Customization",
          theory:
            "Custom-built mobile applications tailored for Android and iOS with advanced features, user-friendly designs, and scalability.",
          image: "/images/services/MobileApp.png",
          imagePosition: "right",
        },
        {
          title: "Hardware Services",
          theory:
            "Professional IT hardware supply, configuration, and maintenance services to keep your infrastructure reliable and high-performing.",
          image: "/images/home/Hardware.jpg",
          imagePosition: "left",
        },
        {
          title: "Custom ERP Solutions",
          theory:
            "Tailor-made ERP systems designed to align with your unique business workflows, scale with growth, and deliver long-term efficiency.",
          image: "/images/services/ERPSolution.png",
          imagePosition: "right",
        },
      ],
    },
    ar: {
      sectionTitle: "لماذا تختار إنفينكس سوليوشن؟",
      sectionSubtitle: "شريكك الموثوق للتميز التكنولوجي.",
      reasons: [
        {
          icon: faPuzzlePiece,
          title: "حلول شاملة",
          desc: "نقدم حلول تكنولوجيا معلومات متكاملة تحت سقف واحد، مما يلغي الحاجة إلى مزودين متعددين ويضمن التنفيذ السلس.",
        },
        {
          icon: faChartLine,
          title: "سجل حافل بالنجاح",
          desc: "بفضل سنوات الخبرة ومشاريع ناجحة عديدة، نتجاوز توقعات العملاء باستمرار في مختلف الصناعات والتقنيات.",
        },
        {
          icon: faHeadset,
          title: "دعم العملاء 24/7",
          desc: "فريق الدعم الخبير لدينا متاح على مدار الساعة لحل مشكلاتك التقنية بسرعة وضمان استمرار عملك دون انقطاع.",
        },
        {
          icon: faDollarSign,
          title: "أسعار تنافسية",
          desc: "نوفر خدمات عالية الجودة بأسعار مناسبة تضمن عائد استثمار مرتفع دون التضحية بالجودة أو الأداء.",
        },
      ],
      services: [
        {
          title: "خوادم VPS وخدمات السحابة",
          theory:
            "استضافة VPS موثوقة وبنية تحتية سحابية قابلة للتوسع مع أمان على مستوى المؤسسات، تخصيص كامل، ووقت تشغيل بنسبة 99.9% لضمان استمرار عملك دون انقطاع.",
          image: "/images/services/VPS.png",
          imagePosition: "left",
        },
        {
          title: "روبوت واتساب والتواصل",
          theory:
            "روبوتات واتساب ذكية مدعومة بالذكاء الاصطناعي وتكامل واجهة برمجة التطبيقات التجارية لتبسيط خدمة العملاء وزيادة التفاعل وجذب العملاء بسهولة.",
          image: "/images/home/WhatsApp.png",
          imagePosition: "right",
        },
        {
          title: "تنفيذ واستشارات ERP",
          theory:
            "تنفيذ شامل لأنظمة ERP مع استشارات متخصصة لتحسين العمليات وزيادة الإنتاجية وتحقيق التحول الرقمي بسلاسة.",
          image: "/images/services/ERPConsulting.png",
          imagePosition: "left",
        },
        {
          title: "أتمتة الذكاء الاصطناعي",
          theory:
            "حلول أتمتة متقدمة بالذكاء الاصطناعي تقلل من العمل اليدوي، تزيد الكفاءة، وتمكّن عملك من اتخاذ قرارات أذكى.",
          image: "/images/services/AIAutomation.jpg",
          imagePosition: "right",
        },
        {
          title: "تطوير مواقع الويب",
          theory:
            "مواقع ويب حديثة ومتجاوبة وصديقة لمحركات البحث مصممة لتوفير تجربة مستخدم استثنائية وتعزيز وجودك الرقمي.",
          image: "/images/services/Mobile.png",
          imagePosition: "left",
        },
        {
          title: "تطوير وتخصيص تطبيقات الموبايل",
          theory:
            "تطبيقات موبايل مخصصة لأندرويد وiOS بميزات متقدمة وتصاميم سهلة الاستخدام وقابلة للتوسع.",
          image: "/images/services/MobileApp.png",
          imagePosition: "right",
        },
        {
          title: "خدمات الأجهزة",
          theory:
            "توفير وصيانة وتهيئة أجهزة تقنية المعلومات باحترافية للحفاظ على بنية تحتية موثوقة وعالية الأداء.",
          image: "/images/home/Hardware.jpg",
          imagePosition: "left",
        },
        {
          title: "حلول ERP مخصصة",
          theory:
            "أنظمة ERP مصممة خصيصًا لتناسب سير عملك الفريد، قابلة للتوسع مع نمو أعمالك، وتوفر كفاءة طويلة المدى.",
          image: "/images/services/ERPSolution.png",
          imagePosition: "right",
        },
      ],
    },
  };

  const currentLang = translations[language] || translations.en;

  const blueGradient = "linear-gradient(to right, #6372ff 0%, #5ca9fb 100%)";
  const textColor = darkMode ? "#e0e0e0" : "#1a2526";
  const bgColor = darkMode ? "rgba(1,8,34,255)" : "#eee";

  return (
    <section
      id="whyus"
      style={{
        backgroundColor: bgColor,
        padding: "80px 20px",
        color: textColor,
        fontFamily:
          language === "ar" ? "'Amiri', sans-serif" : "'Raleway', sans-serif",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* Section Title */}
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <h2
            style={{
              fontWeight: 800,
              fontSize: 36,
              marginBottom: 10,
              textTransform: "uppercase",
              display: "inline-block",
              position: "relative",
              paddingBottom: 15,
            }}
          >
            {currentLang.sectionTitle}
            <span
              style={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                height: 4,
                width: 60,
                background: blueGradient,
                borderRadius: 2,
              }}
            />
          </h2>
          <p
            style={{
              fontSize: 23,
              maxWidth: 600,
              margin: "10px auto 0",
            }}
          >
            {currentLang.sectionSubtitle}
          </p>
        </div>

        {/* Reasons */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 30,
            marginBottom: 80,
          }}
        >
          {currentLang.reasons.map(({ icon, title, desc }, i) => (
            <div
              key={i}
              className="reason-card"
              style={{
                background: darkMode ? "#2c3e50" : "#fff",
                borderRadius: 15,
                padding: 30,
                textAlign: "center",
                color: darkMode ? "#fff" : textColor,
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
            >
              <div
                style={{
                  background: blueGradient,
                  color: "#fff",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  fontSize: 36,
                }}
              >
                <FontAwesomeIcon icon={icon} />
              </div>
              <h3 style={{ marginBottom: 15 }}>{title}</h3>
              <p style={{ fontSize: 14 }}>{desc}</p>
            </div>
          ))}
        </div>

        {/* Services */}
        {currentLang.services.map(({ title, theory, image, imagePosition }, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              flexDirection: imagePosition === "left" ? "row" : "row-reverse",
              alignItems: "center",
              gap: 40,
              marginBottom: 60,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <div
              className="image-container"
              style={{
                width: "100%",
                maxWidth: 400,
                borderRadius: 20,
                overflow: "hidden",
                transition: "transform 0.3s ease, filter 0.3s ease",
              }}
            >
              <img
                src={image}
                alt={title}
                style={{
                  width: "100%",
                  borderRadius: 20,
                  display: "block",
                }}
              />
            </div>
            <div
              style={{
                maxWidth: 600,
                textAlign: imagePosition === "left" ? "left" : "right",
              }}
            >
              <h3
                style={{
                  fontSize: 28,
                  marginBottom: 15,
                  position: "relative",
                  display: "inline-block",
                  paddingBottom: 10,
                }}
              >
                {title}
                <span
                  style={{
                    position: "absolute",
                    bottom: 0,
                    [imagePosition]: 0,
                    height: 3,
                    width: 50,
                    background: blueGradient,
                  }}
                />
              </h3>
              <p style={{ fontSize: 18, lineHeight: "1.6" }}>{theory}</p>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        .reason-card:hover {
          transform: translateY(-5px) scale(1.03);
          box-shadow: ${darkMode
            ? "0 8px 20px rgba(30, 144, 255, 0.5)"
            : "0 8px 20px rgba(0, 0, 0, 0.15)"};
        }
        .image-container:hover {
          transform: scale(1.05);
        }
        .image-container img {
          transition: transform 0.3s ease;
        }
        .image-container:hover img {
          transform: scale(1.1);
        }
      `}</style>
    </section>
  );
};

export default WhyUs;